import React, { useState, useEffect, Suspense, lazy } from 'react';
import './style/main.css';
import './components/button/style.css';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  NavLink,
  useLocation,
} from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import Navbar from './components/navbar/Navbar';
import Footer from './components/footer/Footer';
import PageSEO from './components/PageSEO';
import PageLoader from './components/pageLoader/PageLoader';
// Home is loaded eagerly -- it's the page most visitors land on first,
// and it must not wait on an extra chunk round-trip. Every other route is
// code-split: its JS (and whatever heavy libraries only it uses -- GSAP,
// PhotoSwipe, typed.js, lucide-react, etc.) is fetched only when that
// route is actually visited.
import Home from './pages/Home';
import ScrollToTop from './utils/scrollToTop';
import MobileNav from './components/mobileNav/MobileNav';
import CookieBanner from './components/cookieBanner/CookieBanner';
import CookieSettingsModal from './components/cookieBanner/CookieSettingsModal';
import CookieSettingsButton from './components/cookieBanner/CookieSettingsButton';
import { getCookieConsent } from './helpers/cookieConsent';

const Projects = lazy(() => import('./pages/Projects'));
const Contacts = lazy(() => import('./pages/Contacts'));
const Project = lazy(() => import('./pages/Project'));
const Gallery = lazy(() => import('./pages/Gallery'));
const PricePage = lazy(() => import('./pages/PricePage'));
const PackagePage = lazy(() => import('./pages/PackagePage'));
const DesignPage = lazy(() => import('./pages/DesignPage'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const TermsOfUse = lazy(() => import('./pages/TermsOfUse'));

const NotFound = () => {
  // Self-canonicalize to the actual (invalid) path that was requested,
  // instead of a fabricated "/404" address that isn't a real resource.
  const { pathname } = useLocation();

  return (
    <main style={{ minHeight: '70vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1.5rem', padding: '4rem 2rem', textAlign: 'center' }}>
      <PageSEO title="404 — Page Not Found | PixelCraft" path={pathname} noindex omitCanonical />
      <h1 style={{ fontSize: '5rem', fontWeight: 700, margin: 0, opacity: 0.15 }}>404</h1>
      <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)' }}>Page not found</p>
      <NavLink to="/" className="button">Go Home</NavLink>
    </main>
  );
};

// Sprint 8.1 readiness signal for scripts/build/prerender.js. Polls for the
// route-level Suspense fallback (`.page-loader`) to disappear -- which only
// happens once the lazy chunk *and* its i18next namespace have both
// resolved and the real page has mounted -- then waits one more animation
// frame for react-helmet-async to flush (title/canonical/JSON-LD are
// committed via its own rAF-scheduled side effect, one tick behind React's
// own commit) before publishing status. Deliberately NOT gated on "JSON-LD
// exists": noindex routes (care-plan, 404) never render a JSON-LD script at
// all and would hang forever waiting for one.
// Not read anywhere at runtime by the app itself -- purely a build-time
// signal for the prerender script, harmless for real visitors.
function usePrerenderStatusSignal() {
  const location = useLocation();
  const { i18n } = useTranslation('common');

  useEffect(() => {
    delete window.__PRERENDER_STATUS__;
    let cancelled = false;

    const publish = () => {
      if (cancelled) return;
      window.__PRERENDER_STATUS__ = {
        ready: true,
        route: location.pathname,
        language: i18n.resolvedLanguage,
      };
    };

    const waitForLoaderGone = () => {
      if (cancelled) return;
      if (document.querySelector('.page-loader')) {
        requestAnimationFrame(waitForLoaderGone);
        return;
      }
      // One more frame for Helmet's own async commit.
      requestAnimationFrame(publish);
    };

    requestAnimationFrame(waitForLoaderGone);
    return () => {
      cancelled = true;
    };
  }, [location.pathname, i18n.resolvedLanguage]);
}

function AnimatedRoutes() {
  const location = useLocation();
  usePrerenderStatusSignal();

  return (
    <Suspense fallback={<PageLoader />}>
      {/* initial={false}: every route below is wrapped in its own
          motion.div with initial={{opacity:0}} (for the page-fade
          transition). Without this, that opacity:0 is the actual inline
          style captured in prerendered HTML and seen by a no-JS visitor --
          the entire page content, on every route, would be invisible.
          initial={false} makes AnimatePresence skip the enter animation
          only for whatever is already mounted when it first appears (i.e.
          the very first page load); real client-side route changes still
          fade normally. */}
      {/* data-route-suspense-boundary + display:contents: a stable, layout-
          invisible marker for scripts/build/prerender.js. This Suspense boundary's
          resolved content was never produced by react-dom/server, so the
          prerendered HTML lacks the `<!--$-->`/`<!--/$-->` comment pair
          react-dom/server always writes around a resolved Suspense
          boundary's children (see pushTextInstance's sibling,
          renderDOMElement's suspense handling, in react-dom-server). Without
          it, hydrateRoot()'s tryToClaimNextHydratableInstance() can't find a
          dehydrated boundary here, drops out of hydration mode for this
          fiber AND every later sibling in the tree (Footer, CookieBanner,
          ...), and inserts everything fresh -- confirmed via
          scripts/debug/debug-hydration.js as the cause of the #418/#423 mismatch
          on every single route. This element gives the prerender script an
          unambiguous, content-independent anchor to wrap with those two
          comments; display:contents keeps it out of layout/flex/grid
          entirely so it changes nothing visually. */}
      <div data-route-suspense-boundary="" style={{ display: 'contents' }}>
      <AnimatePresence mode="wait" initial={false}>
        <Routes location={location} key={location.pathname}>
        <Route
          path="/"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Home />
            </motion.div>
          }
        />

        <Route
          path="/prices"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <PricePage />
            </motion.div>
          }
        />

        <Route
          path="/package/:slug"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <PackagePage />
            </motion.div>
          }
        />
        <Route
          path="/care-plan/:slug"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <PackagePage />
            </motion.div>
          }
        />

        <Route
          path="/projects"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Projects />
            </motion.div>
          }
        />
        <Route
          path="/project/:id"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Project />
            </motion.div>
          }
        />
        <Route
          path="/design"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <DesignPage />
            </motion.div>
          }
        />
        <Route
          path="/gallery"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Gallery />
            </motion.div>
          }
        />
        <Route
          path="/contacts"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Contacts />
            </motion.div>
          }
        />
        <Route
          path="/privacy-policy"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <PrivacyPolicy />
            </motion.div>
          }
        />
        <Route
          path="/cookie-policy"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <CookiePolicy />
            </motion.div>
          }
        />
        <Route
          path="/terms-of-use"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <TermsOfUse />
            </motion.div>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </AnimatePresence>
      </div>
    </Suspense>
  );
}

function App() {
  // Explicit namespace: a bare useTranslation() falls back to i18next's
  // hardcoded default namespace name "translation" rather than our
  // configured defaultNS, which made the backend try to fetch a
  // /locales/{lng}/translation.json that doesn't exist -- from the App
  // root, above the Suspense boundary, on every load.
  const { i18n } = useTranslation('common');
  const [navOpen, setNavOpen] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(() => getCookieConsent() === null);
  const [cookieSettingsOpen, setCookieSettingsOpen] = useState(false);

  // Keep <html lang> in sync with the active i18next language so it never
  // says "en" while the rendered content is actually ru/fi (or vice versa).
  useEffect(() => {
    document.documentElement.lang = i18n.resolvedLanguage || 'en';
  }, [i18n.resolvedLanguage]);

  // Sprint 8.1: prerendered HTML is always built in one fixed language
  // (src/i18n.js reads data-prerendered-lang and forces i18next to start
  // there, so first hydration never mismatches the markup). Once hydration
  // has committed, apply the visitor's *actual* saved/browser preference,
  // if it differs -- as one deliberate follow-up update, never folded into
  // the first render. Runs once; not on every language change.
  useEffect(() => {
    const rootEl = document.getElementById('root');
    if (rootEl?.dataset.prerendered !== 'true') return;
    const prerenderedLang = rootEl.dataset.prerenderedLang;
    const supported = ['en', 'ru', 'fi'];
    const stored = window.localStorage.getItem('i18nextLng');
    const preferred = (stored || navigator.language || '').slice(0, 2);
    if (supported.includes(preferred) && preferred !== prerenderedLang) {
      i18n.changeLanguage(preferred);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNav = () => setNavOpen(true);
  const closeNav = () => setNavOpen(false);

  const openCookieSettings = () => setCookieSettingsOpen(true);
  const closeCookieSettings = () => setCookieSettingsOpen(false);
  const handleConsentSaved = () => {
    setBannerVisible(false);
    setCookieSettingsOpen(false);
  };

  return (
    <HelmetProvider>
      <div className="App">
        <Router>
          <ScrollToTop />
          <Navbar openNav={openNav} />
          <MobileNav navOpen={navOpen} closeNav={closeNav} />
          <AnimatedRoutes />
          <Footer openCookieSettings={openCookieSettings} />
          <CookieBanner
            visible={bannerVisible}
            onDecision={() => setBannerVisible(false)}
            onOpenSettings={openCookieSettings}
          />
          <CookieSettingsButton visible={!bannerVisible} onClick={openCookieSettings} />
          <CookieSettingsModal
            isOpen={cookieSettingsOpen}
            onClose={closeCookieSettings}
            onSaved={handleConsentSaved}
          />
        </Router>
      </div>
    </HelmetProvider>
  );
}

export default App;
